import {apiFetch} from '~/composables/api/apiFetch';
import type {Organization} from '~/types/api';
import {validateRedirectUrl} from '~/utils/redirectValidation';
import {parseInviteToken} from '~/utils/parseInviteToken';
import {useFrontendApisClient} from '~/composables/auth/useFrontendApisClient';
import {getNewSignupFlowDate} from '~/utils/featureFlags';

// localStorage key used by partner integration redirect links (rt= param).
const PARTNER_RT_KEY = 'vector_rt_redirect';

// Type for PropelAuth client
interface PropelAuthClient {
  getAuthenticationInfoOrNull: (_forceRefresh?: boolean) => Promise<{user: unknown} | null>;
}

// Routes that don't require authentication. Custom signup/login UI lives here so
// users can reach them while logged out without being redirected. /logout is in
// the list so authed users can hit it without the middleware running the org
// bootstrap before the page has a chance to clear their session.
const PUBLIC_ROUTES = ['/login', '/signup', '/auth/callback', '/logout', '/pixel'];

const isPublicRoute = (path: string) =>
  PUBLIC_ROUTES.some(p => path === p || path.startsWith(`${p}/`));

// Request deduplication cache to prevent race conditions
// when multiple page navigations trigger this middleware simultaneously
interface OrgRequestCache {
  promise: Promise<Organization> | null;
  timestamp: number;
  TTL: number;
}

const orgRequestCache: OrgRequestCache = {
  promise: null,
  timestamp: 0,
  TTL: 5000,
};

export const clearOrgCache = () => {
  orgRequestCache.promise = null;
  orgRequestCache.timestamp = 0;
};

const E2E_VISUAL_TEST_ORG_ID = 'e2e-visual-test-org-id';

const mockOrgForE2E: Organization = {
  id: 1,
  name: 'E2E Visual Test Org',
  created_at: '2024-01-01T00:00:00.000Z',
  domain: null,
  stripe_customer_id: 'cus_e2e_visual_test',
  stripe_subscription_id: 'sub_e2e_visual_test',
  propelauth_id: 'pa_e2e_visual_test',
  uuid: E2E_VISUAL_TEST_ORG_ID,
  is_pixel_installed: false,
  is_pixel_fallback_enabled: false,
  svix_app_id: null,
  svix_integration_key: null,
  settings: {isDemoOrg: true},
  in_market_topics: [],
  is_disabled: false,
  disabled_at: null,
  disabled_reason: null,
  subscription: {
    items: {
      data: [
        {
          price: {
            product: 'prod_QhnUryTPVYCPED',
            id: 'price_1Qybm2L9PCat5EZ70qAhn7lk',
            recurring: {interval: 'year'},
          },
          quantity: 1,
        },
      ],
    },
    current_period_end: 1893456000,
  },
} as unknown as Organization;

export default defineNuxtRouteMiddleware(async to => {
  const config = useRuntimeConfig().public;
  const e2eVisualTest = !!config.e2eVisualTest;

  if (e2eVisualTest) {
    useState<Organization>('org', () => mockOrgForE2E);
    return;
  }

  if (import.meta.client) {
    const {$propelAuth} = useNuxtApp();
    const propelAuth = $propelAuth as PropelAuthClient;
    // Capture before any await — the client caches after first call so this is
    // safe even if the injection context drops after the auth await below.
    const apiClient = useFrontendApisClient();
    const user = await propelAuth.getAuthenticationInfoOrNull();

    // PropelAuth invite token: existing authenticated users must call joinOrg
    // to accept their invitation. New users are handled in signup.vue instead.
    const inviteTokenParam =
      typeof to.query.invite_token === 'string' ? to.query.invite_token : null;
    if (inviteTokenParam && user) {
      const invite = parseInviteToken(inviteTokenParam);
      if (invite) {
        let joinedOrgId: string | null = null;
        const response = await apiClient.joinOrg(invite.orgId);
        response.handle({
          success: () => {
            joinedOrgId = invite.orgId;
          },
          orgNotFound: () => {},
          orgsNotEnabled: () => {},
          userAlreadyInTooManyOrgs: () => {},
          orgMaxUsersLimitExceeded: () => {},
          unauthorized: () => {},
          emailNotConfirmed: () => {},
          unexpectedOrUnhandled: () => {},
        });
        // Refresh so orgHelper picks up the new membership.
        await propelAuth.getAuthenticationInfoOrNull(true);
        // Switch the active org to the one they just joined so they land in
        // the right workspace instead of their first/default org.
        if (joinedOrgId) {
          try {
            localStorage.setItem('activeOrg', joinedOrgId);
          } catch (_) {
            /* noop */
          }
        }
      }
      // Strip invite_token and e from the URL regardless of join outcome.
      const {invite_token: _it, e: _e, ...rest} = to.query as Record<string, string>;
      return navigateTo({path: to.path, query: rest}, {replace: true});
    }

    // Partner integration deep-link: ?rt=<base64 encoded target URL>
    // Partners send users here (e.g. https://auth.vector.co?rt=BASE64).
    // Decode, validate against the domain whitelist, store in localStorage so
    // the post-login/post-signup flow can pick it up, then either redirect
    // immediately (already authed) or fall through to the normal login gate.
    const rtParam = to.query.rt;
    if (typeof rtParam === 'string' && rtParam) {
      let decoded: string | null = null;
      try {
        decoded = validateRedirectUrl(atob(rtParam));
      } catch (_) {
        /* invalid base64 — ignore */
      }
      if (decoded) {
        try {
          localStorage.setItem(PARTNER_RT_KEY, decoded);
        } catch (_) {
          /* noop */
        }
        if (user) {
          // Already logged in — skip login and go straight to the integration.
          return navigateTo(decoded, {external: decoded.startsWith('http')});
        }
      }
      // Not authed (or invalid rt) — strip the rt param and send to login.
      // The stored PARTNER_RT_KEY value is consumed after successful login.
      return navigateTo('/login', {replace: true});
    }

    if (isPublicRoute(to.path)) {
      // Authenticated users hitting /login or /signup get bounced to the app.
      if (user && (to.path === '/login' || to.path === '/signup')) {
        return navigateTo('/');
      }
      return;
    }

    if (!user) {
      // Check whether the user has a partial session (e.g. TWO_FACTOR_REQUIRED
      // after a magic-link click). If so, send them to /auth/callback which
      // knows how to show the 2FA form. Otherwise send them to /login.
      try {
        const stateResponse = await apiClient.fetchLoginState();
        let loginState: string | null = null;
        stateResponse.handle({
          success: (data: {login_state: string}) => {
            loginState = data.login_state;
          },
          unexpectedOrUnhandled: () => {
            /* noop */
          },
        });
        if (loginState === 'TwoFactorRequired') {
          return navigateTo('/auth/callback', {replace: true});
        }
      } catch (_) {
        /* noop */
      }
      // Invitation links should land on signup (new user path), not login.
      const inviteParam = typeof to.query.invite_token === 'string' ? to.query.invite_token : null;
      if (inviteParam) {
        const eParam = typeof to.query.e === 'string' ? to.query.e : null;
        return navigateTo({
          path: '/signup',
          query: {invite_token: inviteParam, ...(eParam ? {e: eParam} : {})},
        });
      }

      const redirect = to.fullPath && to.fullPath !== '/' ? to.fullPath : undefined;
      return navigateTo({path: '/login', query: redirect ? {redirect} : undefined});
    }

    let org: Organization | null | undefined;
    const now = Date.now();
    const hasOrgCreationTtlRequest =
      orgRequestCache.promise && now - orgRequestCache.timestamp < orgRequestCache.TTL;

    if (hasOrgCreationTtlRequest) {
      try {
        org = await orgRequestCache.promise;
      } catch (_) {
        orgRequestCache.promise = null;
        orgRequestCache.timestamp = 0;
      }
    }

    if (!org) {
      orgRequestCache.timestamp = now;
      orgRequestCache.promise = (async () => {
        const termsAccepted = localStorage.getItem('vector_terms_accepted') === '1';
        const pendingGoal = localStorage.getItem('vector_pending_feature');
        await apiFetch('/orgs', {
          method: 'POST',
          body: {
            referral: (window as any).Rewardful?.referral,
            allowFree: to.query.allow_free,
            goal: pendingGoal || 'ad_reveal',
            termsAccepted,
          },
        });
        // Clear only after the bootstrap succeeded so a failed request retries
        // with the same flags on the next navigation instead of losing them.
        try {
          if (termsAccepted) {
            localStorage.removeItem('vector_terms_accepted');
          }
          if (pendingGoal) {
            localStorage.removeItem('vector_pending_feature');
          }
        } catch (_) {
          /* noop */
        }

        const result = (await apiFetch('/orgs')) as Organization;
        setTimeout(() => {
          if (orgRequestCache.timestamp === now) {
            orgRequestCache.promise = null;
          }
        }, orgRequestCache.TTL);
        return result;
      })();

      try {
        org = await orgRequestCache.promise;
      } catch (error) {
        orgRequestCache.promise = null;
        orgRequestCache.timestamp = 0;
        throw error;
      }
    }

    useState<Organization>('org', () => org!);

    // Terms gate uses the same cutoff as the onboarding page so existing
    // customers (created before the flag date) are never redirected for terms.
    const newSignupFlowDate = await getNewSignupFlowDate();
    const needsTermsRedirect =
      new Date(org.created_at) > new Date(newSignupFlowDate) && !org.settings?.terms_accepted;

    if (
      // make sure user who doesn't have a subscription is redirected to onboarding so they can see the payment block
      (!org.stripe_subscription_id && !to.path.startsWith('/onboarding')) ||
      (needsTermsRedirect &&
        !to.path.startsWith('/onboarding') &&
        !to.path.startsWith('/partner-integrations') &&
        !to.path.startsWith('/integrations/'))
    ) {
      // A pending partner redirect takes priority over onboarding — the partner
      // URL carries allow_free which creates the subscription on arrival.
      try {
        const pendingRt = localStorage.getItem(PARTNER_RT_KEY);
        if (pendingRt) {
          localStorage.removeItem(PARTNER_RT_KEY);
          window.location.href = pendingRt;
          return;
        }
      } catch (_) {
        /* noop */
      }
      window.location.href = '/onboarding';
    }
  }
});
